import React, { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { TIMEZONES, CURRENCIES, SMS_TRANSACTIONAL_CONSENT_TEXT, SMS_PROMO_CONSENT_TEXT } from "@/lib/constants";
import { toast } from "sonner";
import { Bell, LogOut, Mail, MessageSquareText } from "lucide-react";
import { SmsConsentRow } from "@/components/SmsConsentCard";

export default function Settings() {
  const { user } = useOutletContext();
  const { logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [tz, setTz] = useState(user?.timezone || "UTC");
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [saving, setSaving] = useState(false);
  const [consentUser, setConsentUser] = useState(user);
  const onConsentUser = (u) => { setConsentUser(u); setUser(u); };
  const fallbackPhone = consentUser?.sms_consent?.phone || consentUser?.sms_promo_consent?.phone;

  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzOptions = Array.from(new Set([detected, ...TIMEZONES].filter(Boolean)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { timezone: tz, currency });
      setUser(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const dirty = tz !== user?.timezone || currency !== user?.currency;

  return (
    <div className="space-y-6 animate-fade-up max-w-2xl">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-ink-secondary mt-1">Your account details and preferences.</p>
      </div>

      <div className="border rounded-2xl bg-white p-6 space-y-5" data-testid="settings-profile">
        <h2 className="font-heading font-bold">Profile</h2>
        <div className="flex items-center gap-3 py-1">
          <span className="p-2 rounded-lg bg-oatmeal text-ink-secondary"><Mail size={16} /></span>
          <div><p className="text-xs text-ink-muted">Email</p><p className="text-sm font-medium">{user?.email}</p></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Timezone</Label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>{tzOptions.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-ink-muted">Determines how Due Today & Overdue are calculated.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={save} disabled={!dirty || saving} data-testid="settings-save">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="border rounded-2xl bg-white p-6 flex items-center justify-between" data-testid="settings-notifications-link">
        <div className="flex items-center gap-3">
          <span className="p-2 rounded-lg bg-oatmeal text-ink-secondary"><Bell size={16} /></span>
          <div><p className="text-sm font-semibold">Text notifications</p><p className="text-xs text-ink-muted">Manage phone number & alert types</p></div>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard/notifications")} data-testid="go-notifications">Manage</Button>
      </div>

      <div className="border rounded-2xl bg-white p-6" data-testid="settings-sms-consent">
        <div className="flex items-center gap-3 mb-4">
          <span className="p-2 rounded-lg bg-oatmeal text-ink-secondary"><MessageSquareText size={16} /></span>
          <div>
            <p className="text-sm font-semibold">Text message consent</p>
            <p className="text-xs text-ink-muted">Turn each type on or off. You can also reply STOP to any message.</p>
          </div>
        </div>
        <div className="divide-y border-t pt-4">
          <SmsConsentRow kind="transactional" title="Payment alerts & account notifications"
            text={SMS_TRANSACTIONAL_CONSENT_TEXT} consent={consentUser?.sms_consent}
            fallbackPhone={fallbackPhone} onUser={onConsentUser} />
          <SmsConsentRow kind="promotional" title="Promotional messages"
            text={SMS_PROMO_CONSENT_TEXT} consent={consentUser?.sms_promo_consent}
            fallbackPhone={fallbackPhone} onUser={onConsentUser} />
        </div>
      </div>

      <div className="border rounded-2xl bg-white p-6 flex items-center justify-between">
        <div><p className="text-sm font-semibold">Sign out</p><p className="text-xs text-ink-muted">Log out of PaySignal on this device</p></div>
        <Button variant="outline" onClick={async () => { await logout(); navigate("/login"); }} data-testid="settings-logout">
          <LogOut size={16} className="mr-1" /> Log out
        </Button>
      </div>
    </div>
  );
}
